import type { ComponentProps } from "react"
import clsx from "clsx"

import { Image } from "~/components/ui/Image"
import type { TPushEdito } from "~/components/ui/PushEdito/_data/serializer"
import RichText from "~/components/ui/RichText"
import RoundedCta from "~/components/ui/RoundedCta"
import { VideoWithRatio } from "~/components/ui/VideoMobileDesktop"
import WrapperWithLink from "~/components/abstracts/WrapperWithLink"

import { sprinkles } from "~/styles/sprinkles.css"
import { hideFrom, showFrom } from "~/styles/utils/show-hide"
import { text, type TextVariants } from "~/styles/variants"

import * as css from "./styles.css"

export type PushEditoProps = Omit<ComponentProps<"div">, "title"> & TPushEdito

function PushEdito({
  className,
  style,
  title,
  subtitle,
  tag,
  cta,
  bgColor,
  textTheme,
  bgImage,
  videos,
}: PushEditoProps) {
  const textColor: NonNullable<TextVariants>["color"] = textTheme === "black" ? "blue-deep" : "grey-bg-page"
  const backgroundColor = !bgImage ? bgColor ?? "transparent" : "transparent"

  return (
    <WrapperWithLink
      link={cta}
      className={clsx(className, css.PushEdito)}
      style={{ ...style, backgroundColor }}
      data-comp="UI/PushEdito"
    >
      {videos.desktop && videos.mobile ? (
        <div className={css.videos}>
          <VideoWithRatio
            className={clsx(hideFrom("mobile", "block"), showFrom("tablet", "block"), sprinkles({ height: "100%" }))}
            ratio={"1/3"}
            video={{ ...videos.desktop, fill: true }}
          />
          <VideoWithRatio
            className={clsx(hideFrom("tablet", "block"), showFrom("mobile", "block"), sprinkles({ height: "100%" }))}
            ratio={"1/1"}
            video={{ ...videos.mobile, fill: true }}
          />
        </div>
      ) : (
        bgImage && (
          <Image
            sizesFromBreakpoints={[{ ratio: 1 }, { ratio: 1 / 3, breakpoint: "md" }]}
            objectFit="cover"
            className={sprinkles({ position: "absolute", inset: 0 })}
            imageHeight="100%"
            asPlaceholder
            {...bgImage}
          />
        )
      )}
      <div className={sprinkles({ flex: "1" })}>
        {tag && (
          <span
            className={clsx(
              sprinkles({ zIndex: 5, mB: 20, display: "block", position: "relative" }),
              text({ design: "spezia-18", color: textColor, transform: "uppercase" })
            )}
          >
            {tag}
          </span>
        )}
      </div>
      <div className={css.EditoText}>
        <RichText
          render={title}
          className={clsx(
            sprinkles({ overflowWrap: "break-word" }),
            text({
              design: "spezia-22-48-fluid",
              lineHeight: 1,
              transform: "uppercase",
              color: textColor,
              textAlign: "center",
            })
          )}
        />
        <RichText
          render={subtitle}
          className={clsx(text({ design: "riposte-13-14", lineHeight: 1.45, color: textColor, textAlign: "center" }))}
        />
      </div>
      {cta && (
        <div className={css.bottom}>
          <RoundedCta asDiv className={sprinkles({ mT: 30, zIndex: 5 })} {...cta} />
        </div>
      )}
    </WrapperWithLink>
  )
}

export default PushEdito
